
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_URL, IMAGE_HOST } from "../constants";

const NEWS_COLORS = [
  ["bg-red-600", "text-red-600", "border-red-600", "from-slate-900", "to-slate-700"],
  ["bg-emerald-600", "text-emerald-600", "border-emerald-600", "from-emerald-900", "to-teal-700"],
  ["bg-amber-600", "text-amber-600", "border-amber-600", "from-orange-900", "to-amber-700"],
  ["bg-purple-600", "text-purple-600", "border-purple-600", "from-purple-900", "to-violet-700"],
];

function unwrapNews(response) {
  const payload = response?.data ?? response;
  const nested = payload?.data ?? payload;
  return Array.isArray(nested) ? nested : nested?.items ?? nested?.news ?? nested?.results ?? [];
}

function relativeTime(value, now) {
  if (!value) return "";
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "";
  const minutes = Math.max(0, Math.floor(((now ?? Date.now()) - timestamp) / 60000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} wk${weeks === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(days / 365);
  return `${years} yr${years === 1 ? "" : "s"} ago`;
}

function mapArticle(item, index) {
  const color = NEWS_COLORS[index % NEWS_COLORS.length];
  const body = item.excerpt ?? item.summary ?? item.preview ?? item.content ?? "";
  const date = item.publishedAt ?? item.published_at ?? item.published_date ?? item.createdAt ?? item.created_at;
  const cover = item.coverImage ?? item.cover_image ?? item.imageUrl ?? item.image_url ?? item.image ?? "";
  return {
    id: item.id ?? item._id ?? item.slug ?? index,
    slug: item.slug ?? item.id ?? item._id,
    category: item.category?.name ?? item.categoryName ?? item.category_name ?? (typeof item.category === "string" ? item.category : "Latest News"),
    categoryColor: color[0], textColor: color[1], borderColor: color[2],
    gradientFrom: color[3], gradientTo: color[4],
    title: item.title ?? item.name ?? "News",
    author: item.author?.name ?? item.authorName ?? item.author_name ?? "Fillop",
    publishedAt: date,
    readTime: item.readTime ?? item.read_time ?? "",
    preview: String(body).replace(/<[^>]*>/g, "").slice(0, 180),
    image: cover && /^(https?:)?\/\//i.test(cover) ? cover : cover ? `${IMAGE_HOST || ""}${cover.startsWith("/") ? "" : "/"}${cover}` : "",
  };
}

function ArticleCard({ article, now }) {
  return (
    <article className="bg-white rounded-lg sm:rounded-xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-md transition-shadow duration-300 flex flex-col h-full">
      <div className={`relative bg-gradient-to-br ${article.gradientFrom} ${article.gradientTo} aspect-video flex items-center justify-center`}>
        {article.image ? <img src={article.image} alt={article.title} className="absolute inset-0 h-full w-full object-cover" /> : <svg className="w-8 h-8 sm:w-12 sm:h-12 text-white/10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z" />
        </svg>}
        <span className="absolute left-2 top-2 rounded bg-white/95 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-slate-800 sm:left-3 sm:top-3">
          {article.category}
        </span>
      </div>

      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1 text-xs text-stone-400 mb-2">
          <span className="font-semibold text-stone-500 truncate">{article.author}</span>
                <span className="hidden sm:inline">{"\u00b7"}</span>
          <span className="hidden sm:inline">{relativeTime(article.publishedAt, now)}</span>
        </div>

        <h3 className="font-serif font-bold text-sm sm:text-base leading-snug mb-2 text-stone-900 line-clamp-3">
          {article.title}
        </h3>

        <p className="text-stone-500 text-xs sm:text-sm leading-relaxed line-clamp-2">{article.preview}</p>

        <div className="mt-auto pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <Link
            to={article.slug ? `/blog/${article.slug}` : "/blog"}
            state={{from: "/", article}}
            className={`inline-flex items-center justify-center sm:justify-start gap-1 text-xs font-bold uppercase tracking-widest ${article.textColor} hover:opacity-70 transition`}
          >
            Read More
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </Link>
          <span className="text-xs text-stone-400 text-center sm:text-right">{article.readTime}</span>
        </div>
      </div>
    </article>
  );
}

export default function NewsPage() {
  const [copied, setCopied] = useState(false);
  const [articles, setArticles] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState([]);
  const [newsStatus, setNewsStatus] = useState("loading");
  const [now, setNow] = useState(() => Date.now());
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/public/news?limit=6&sort=newest`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Could not load news");
        return response.json();
      })
      .then((response) => {
        const items = unwrapNews(response);
        if (!active) return;
        if (!Array.isArray(items)) throw new Error("Unexpected news response");
        setArticles(items.map(mapArticle));
        setNewsStatus(items.length ? "ready" : "empty");
      })
      .catch(() => { if (active) setNewsStatus("error"); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/public/news/categories`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Could not load news categories");
        return response.json();
      })
      .then(async (response) => {
        const root = response?.data ?? response;
        const payload = root?.data ?? root;
        const list = Array.isArray(payload) ? payload : payload?.categories ?? payload?.items ?? [];
        const values = list.map((item) => typeof item === "string" ? {name: item} : item).filter((item) => item?.name ?? item?.title);
        if (active) {
          const postsByCategory = await Promise.all(values.map((category, index) => (async () => {
            const name = category.name ?? category.title;
            const candidates = [
              category.slug && ["category", category.slug],
              category.id && ["categoryId", category.id],
              ["category", name],
            ].filter(Boolean);
            for (const [key, value] of candidates) {
              const params = new URLSearchParams({page: "1", limit: "1", sort: "newest", [key]: value});
              const result = await fetch(`${API_URL}/api/public/news?${params}`);
              if (!result.ok) continue;
              const posts = unwrapNews(await result.json());
              if (posts[0]) return mapArticle(posts[0], index);
            }
            return mapArticle({title: `Latest ${name} stories`, category: name, excerpt: `No published ${name} stories are available yet.`}, index);
          })().catch(() => mapArticle({title: `Latest ${category.name ?? category.title} stories`, category: category.name ?? category.title, excerpt: `No published ${category.name ?? category.title} stories are available yet.`}, index))));
          if (active) setCategoryArticles(postsByCategory);
        }
      })
      .catch(() => { if (active) setCategoryArticles([]); });
    return () => { active = false; };
  }, []);

  const hero = articles[0];

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(interval);
  }, []);

  // Auto-scroll every 60 seconds
  useEffect(() => {
    const autoScroll = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const cardWidth = window.innerWidth < 640 ? container.offsetWidth : (window.innerWidth < 1024 ? 384 : 288);
        const gap = window.innerWidth < 640 ? 16 : 20;
        
        container.scrollBy({
          left: cardWidth + gap,
          behavior: 'smooth'
        });
      }
    }, 60000); // 60 seconds

    return () => clearInterval(autoScroll);
  }, []);

  // Manual scroll handlers
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = window.innerWidth < 640 ? container.offsetWidth : (window.innerWidth < 1024 ? 384 : 288);
      const gap = window.innerWidth < 640 ? 16 : 20;
      
      container.scrollBy({
        left: direction === 'next' ? cardWidth + gap : -(cardWidth + gap),
        behavior: 'smooth'
      });
    }
  };

  if (!articles.length) {
    const message = newsStatus === "loading"
      ? "Loading the latest news…"
      : newsStatus === "error"
        ? "News is unavailable right now. Please try again later."
        : "There are no news stories to show right now.";
    return (
      <div className="bg-stone-50 font-sans text-stone-900">
        <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
          <h2 className="mb-4 font-serif text-2xl font-black">Latest News</h2>
          <p role={newsStatus === "error" ? "alert" : undefined} className="mb-8 text-sm text-stone-500">{message}</p>
          <div className="mb-6 flex items-center gap-3">
            <span className="font-inter whitespace-nowrap text-xs font-bold uppercase tracking-widest text-stone-400">Latest Stories</span>
            <div className="h-px flex-1 bg-stone-200" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 min-h-screen font-sans text-stone-900">
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* HERO SECTION */}
        <section className="mb-8 sm:mb-12" data-aos="fade-up">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">
            {/* HERO IMAGE */}
            <div className={`relative overflow-hidden rounded-lg sm:rounded-2xl bg-gradient-to-br ${hero.gradientFrom} ${hero.gradientTo} aspect-video flex items-end shadow-md sm:shadow-xl order-first`} data-aos="fade-right" data-aos-delay="100">
              {hero.image ? (
                <img src={hero.image} alt={hero.title} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-7xl font-black text-white/15">F.</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <span className="relative m-4 rounded bg-white/95 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-800">{hero.category}</span>
            </div>

            {/* HERO CONTENT */}
            <div className="flex flex-col justify-start lg:justify-center" data-aos="fade-left" data-aos-delay="200">
              <span className="text-red-600 text-xs font-bold uppercase tracking-widest mb-2 sm:mb-3">{hero.category}</span>
              
              <h2 className="font-serif font-black text-2xl sm:text-3xl lg:text-4xl leading-tight mb-3 sm:mb-4 text-stone-900">
                {hero.title}
              </h2>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-xs text-stone-400 font-medium mb-3 sm:mb-4">
                <span>By <strong className="text-stone-600">{hero.author}</strong></span>
                <span className="hidden sm:inline">{"\u00b7"}</span>
                <span>{relativeTime(hero.publishedAt, now)}</span>
                <span className="hidden sm:inline">{"\u00b7"}</span>
                <span>{hero.readTime}</span>
              </div>
              
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed mb-4 sm:mb-5">
                {hero.preview}
              </p>
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3" data-aos="fade-up" data-aos-delay="300">
                <Link
                  to={hero.slug ? `/blog/${hero.slug}` : "/blog"}
                  state={{from: "/", article: hero}}
                  className="inline-flex items-center justify-center gap-2 bg-stone-900 hover:bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-5 py-3 rounded transition-colors duration-200"
                >
                  Read More
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </Link>
                
                <button
                  onClick={() => {
                    const shareData = {
                      title: hero.title,
                      text: hero.preview,
                      url: `${window.location.origin}/blog/${hero.slug || hero.id}`,
                    };
                    if (navigator.share) {
                      navigator.share(shareData);
                    } else {
                      navigator.clipboard.writeText(`${window.location.origin}/blog/${hero.slug || hero.id}`).then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      });
                    }
                  }}
                  className="inline-flex items-center justify-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 transition font-medium py-3 sm:py-0"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316" />
                  </svg>
                  {copied ? "Copied!" : "Share"}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION LABEL */}
        <div className="flex items-center gap-3 mb-6 sm:mb-8" data-aos="fade-up" data-aos-delay="400">
          <span className="font-inter text-xs uppercase tracking-widest font-bold text-stone-400 whitespace-nowrap">Latest Stories</span>
          <div className="flex-1 h-px bg-stone-200" />
        </div>

        {/* HORIZONTAL SCROLL CARDS */}
        <section className="mb-4 sm:mb-8" data-aos="fade-up" data-aos-delay="500">
          <div className="relative">
            {/* Previous Button */}
            <button
              onClick={() => scroll('prev')}
              className="absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 z-10 bg-stone-900 hover:bg-sidebar text-white rounded-full p-2 sm:p-3 transition-colors duration-200 shadow-lg"
              aria-label="Previous articles"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Scroll Container */}
            <div 
              ref={scrollContainerRef}
              className="overflow-x-auto scroll-smooth pb-2 pr-4 sm:pr-6" 
              style={{ 
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              {/* Hide scrollbar for webkit browsers (Chrome, Safari, Edge) */}
              <style>{`
                .scroll-container::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              <div className="flex gap-4 sm:gap-5 pl-4 sm:pl-6">
                {(categoryArticles.length ? categoryArticles : articles.slice(1)).map((article) => (
                  <div key={article.id} className="flex-shrink-0 w-full sm:w-96 lg:w-72">
                    <ArticleCard article={article} now={now} />
                  </div>
                ))}
                {!categoryArticles.length && !articles.slice(1).length && <p className="py-8 text-sm text-stone-500">No stories to show right now.</p>}
              </div>
            </div>

            {/* Next Button */}
            <button
              onClick={() => scroll('next')}
              className="absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 z-10 bg-stone-900 hover:bg-sidebar text-white rounded-full p-2 sm:p-3 transition-colors duration-200 shadow-lg"
              aria-label="Next articles"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          
          </div>
        </section>
      </main>
    </div>
  );
}
